/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import EnforceIcon from "@/src/assets/icons/contract/toggleable/IconEnforce";
import AdvisoryIcon from "@/src/assets/icons/contract/toggleable/IconQuestionmarkBubble";
import Person from "@/src/components/Form/Common/Person/Person";
import _includes from "lodash/includes";
import Address from "@/src/components/Form/Common/Address/Address";
import PersonIcon from "@/src/assets/icons/provision/toggleable/IconPerson";
import FamilyIcon from "@/src/assets/icons/generic/toggleable/IconFamilyDetailed";
import BaseEnforceClaim from "./BaseEnforceClaim";
import { isPhoneNumber, minStringLength, requiredValidator } from "@/src/services/validationRules";
import { useForm } from "@/src/provider/Form/FormProvider";
import Group from "@/src/components/Wizard/Group";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase, CaseCategorySelectionMode } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";
import CoinsuredClient from "@/src/components/Form/AbstractLitigationCase/CoinsuredClient";
import { translate } from "@/src/services/translations/translations";
import CivilServiceStatusActiveIcon from "@/src/assets/icons/contract/toggleable/IconContract";
import CivilServiceStatusRetiredIcon from "@/src/assets/icons/employment/toggleable/IconVacation";

type BaseProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  hideCaseCategories?: boolean;
  alternativeHeadline?: string;
};

export default function Base({ submitProps, product, hideCaseCategories = false, alternativeHeadline }: BaseProps) {
  const { values, handleSubmit, handleChange } = useForm();
  const shouldHideCaseCategories = hideCaseCategories || product.caseCategorySelectionMode === CaseCategorySelectionMode.FIXED;

  useEffect(() => {
    if (values.legalRepresentativeType === "me") {
      handleChange({
        target: {
          name: "clientLegalRepresentative.gender",
          value: values.customer.person.gender,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.givenName",
          value: values.customer.person.givenName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.familyName",
          value: values.customer.person.familyName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.streetAddress",
          value: values.customer.person.residenceAddress.streetAddress,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.postalCode",
          value: values.customer.person.residenceAddress.postalCode,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.addressLocality",
          value: values.customer.person.residenceAddress.addressLocality,
          type: "text",
        },
      });
      return;
    }
    handleChange({
      target: {
        name: "clientLegalRepresentative.gender",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.givenName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.familyName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.streetAddress",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.postalCode",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.addressLocality",
        value: "",
        type: "text",
      },
    });
  }, [values.legalRepresentativeType]);

  useEffect(() => {
    if (values.affectedPartyType !== "customer" || values.howManyClients === "oneClient") {
      handleChange({
        target: {
          name: "additionalClients",
          value: [],
        },
      });
      return;
    }
    if (values.howManyClients === "twoClients" && !values.additionalClients?.length) {
      handleChange({
        target: {
          name: "additionalClients",
          value: product.additionalClients?.length
            ? product.additionalClients
            : [
                {
                  givenName: "",
                  familyName: "",
                  gender: "",
                },
              ],
        },
      });
    }
  }, [values.howManyClients, values.affectedPartyType]);

  const getCaseCategoryOptions = () => {
    let options = [
      {
        labelText: "Ich möchte einen Anspruch durchsetzen",
        labelIcon: <EnforceIcon />,
        labelIconActive: <EnforceIcon active />,
        value: "enforceClaim",
        optionHintText:
          "Ich möchte gegen eine Entscheidung vorgehen oder Leistungen einfordern. Beispiele: Die Beihilfe wurde gekürzt. | Ich wurde bei einer Beförderung übergangen oder ungerecht beurteilt. | Meine Besoldung oder Versorgung wurde falsch berechnet",
      },
    ];
    if (product.categoryChange === null || product.categoryChange.previousCaseCategory !== "advisory") {
      options = [
        {
          labelText: "Ich benötige zunächst nur eine Erstberatung",
          labelIcon: <AdvisoryIcon />,
          labelIconActive: <AdvisoryIcon active />,
          value: "advisory",
          optionHintText:
            "Ich möchte nichts weiter veranlassen, sondern habe nur eine kurze Frage im Bereich des Beamtenrechts und benötige hierfür eine Erstberatung.",
        },
        ...options,
      ];
    }
    return options;
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Anliegen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>{alternativeHeadline !== null ? alternativeHeadline : "Allgemeines zu Ihrem Anliegen"}</strong>
              </FormLegend>
            </Grid>
            {!shouldHideCaseCategories && (
              <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                <Group
                  type="radio"
                  name="caseCategory"
                  question="Wie können wir Ihnen helfen?"
                  options={getCaseCategoryOptions()}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}

            {values.caseCategory && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>
                    {values.caseCategory === "advisory"
                      ? "Bitte schildern Sie hier nur kurz in zwei bis drei Sätzen, was passiert ist und was Sie erreichen möchten. Detaillierte Angaben, wie Namen oder Adressen sind für eine Erstberatung zunächst nicht zwingend notwendig."
                      : "Bitte schildern Sie zunächst nur kurz in zwei bis drei Sätzen, was passiert ist und was Sie erreichen möchten. Detaillierte Angaben, wie Namen, Adressen oder weiterführende Informationen zum Sachverhalt, müssen Sie hier noch nicht machen. Diese können Sie in einem späteren Schritt ergänzen."}
                  </FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="initialClientDescriptionAndObjectives"
                    label="Beschreibung und Zielsetzung"
                    placeholder="Die Beihilfestelle hat meine Rechnung vom 12.05. teilweise abgelehnt. Ich möchte dagegen Widerspruch einlegen, damit die vollen Kosten übernommen werden."
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      maxLength: 500,
                    }}
                    multiline
                    rows={8}
                    validators={[requiredValidator("Bitte beschreiben Sie was passiert ist und was Sie erreichen möchten.")]}
                    helperText={
                      "[" +
                      (values.initialClientDescriptionAndObjectives?.length ?? 0) +
                      "/500]" +
                      ((values.initialClientDescriptionAndObjectives?.length ?? 0) < 50 ? " Bitte geben Sie mindestens 50 Buchstaben ein" : "")
                    }
                  />
                </Grid>

                <Person path="customer.person" headline={"Wie heißen Sie?"} />
                {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
                  <Address path="customer.person.residenceAddress" headline={"Wo wohnen Sie?"} />
                )}
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>Wie können wir Sie erreichen?</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="customer.telephone"
                    label="Ihre Telefonnummer"
                    autoComplete="tel"
                    validators={[
                      requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                      {
                        validator: minStringLength,
                        params: [7],
                        message: "Ihre Telefonnummer ist zu kurz",
                      },
                      { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                    ]}
                  />
                </Grid>
                {_includes(["enforceClaim"], values.caseCategory) && (
                  <>
                    <Group
                      type="radio"
                      name="civilServiceStatus"
                      question={translate(`${product.name}.civilServiceStatus.question`, values)}
                      options={[
                        {
                          labelText: translate(`${product.name}.civilServiceStatus.options.active.label`, values),
                          labelIcon: <CivilServiceStatusActiveIcon />,
                          labelIconActive: <CivilServiceStatusActiveIcon active />,
                          value: "active",
                        },
                        {
                          labelText: translate(`${product.name}.civilServiceStatus.options.retired.label`, values),
                          labelIcon: <CivilServiceStatusRetiredIcon />,
                          labelIconActive: <CivilServiceStatusRetiredIcon active />,
                          value: "retired",
                        },
                      ]}
                      validators={[requiredValidator(translate(`${product.name}.employerType.validators.required`, values))]}
                    />

                    <Grid size={12}>
                      <Group
                        type="radio"
                        name="affectedPartyType"
                        question={"Um wen geht es bei Ihrem Anliegen?"}
                        options={[
                          {
                            labelText: "Ich bin selbst betroffen und benötige rechtliche Unterstützung",
                            labelIcon: <PersonIcon />,
                            labelIconActive: <PersonIcon active />,
                            value: "customer",
                          },
                          {
                            labelText: "Eine mir bekannte Person ist betroffen und benötigt rechtliche Unterstützung - zum Beispiel ein Familienmitglied",
                            labelIcon: <FamilyIcon />,
                            labelIconActive: <FamilyIcon active />,
                            value: "coinsured",
                          },
                        ]}
                        validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                      />
                    </Grid>
                    {values.affectedPartyType === "coinsured" && <CoinsuredClient product={product} />}
                  </>
                )}
                {values.caseCategory === "enforceClaim" && <BaseEnforceClaim product={product} />}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
